import { useEffect, useLayoutEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import classnames from "classnames"
import { map, findIndex, get, find, compact } from "lodash"
import { CardContent, Paper } from "@mui/material"
import { PERIOD, DATE, RESOURCE_STATUS } from "helpers/shapes"
import { getTrendsExposureFormatters } from "helpers/formatters/trends"
import { AGGREGATION_SHAPE } from "helpers/factorClassification"
import { computeCSVData } from "helpers/csv"
import { OK } from "helpers/data-statuses"
import AnalyzeHeader from "modules/analyze-header"
import HistoricalExposureDetails from "modules/analyze-exposure-details"
import Litmus from "modules/litmus"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import LoadingWrapper from "components/loading-wrapper"
import NoData from "components/no-data"
import GraphTopBar from "components/graph-top-bar"
import AggregationDropdown, { OPTION_SHAPE } from "components/list-item-dropdown"
import ExcelDownload from "components/excel-download"
import withNavigate from "hocs/withNavigate"
import DefaultMetricUnits from "modules/default-metric-units"
import { METRIC_TYPES_SHAPE } from "helpers/metrics/shapes"
import { METRIC_TYPES } from "helpers/assets/constants"

import "./style.scss"
import { useCheckLastPreferenceRedirect } from "hooks/preferences"
import { useNotifications } from "hooks/notifications"

function AnalyzeHistoricalExposure({
  earliestDate,
  latestDate,
  setDate,
  selectedDate,
  startDate,
  aiSummaryStartDate,
  endDate,
  period,
  datesAdjusted,
  data,
  items,
  displayType,
  options,
  toggle,
  focusChartLine,
  blurChartLine,
  focusedChartLine,
  viewType,
  isActive,
  drift,
  litmusViewVisible,
  fileName,
  resetDate,
  firstDate,
  selectedOption,
  trackCSVDownload,
  only,
  account,
  portfolioId,
  base,
  setPeriod,
  addNotification,
  displayTypeName,
  params: { contentSetId },
  displayAggregationToggle,
  aggregation,
  aggregationOptions,
  setAggregationFn,
  selectedViews,
  openDialog,
  displayAddView,
  availableSelectViewFactors,
  currency,
  isStyleFactorsAdditive,
  isSensitivityContentSet,
  setExposureUnit,
  exposureUnit,
  passthroughResourcesStatus,
  isContentSetAndUnavailable,
  setDisplayType,
  toggleOnly,
  removeSelectedView,
}) {
  const navigate = useNavigate()
  const { wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault } = useCheckLastPreferenceRedirect()
  const notifications = useNotifications()

  useLayoutEffect(() => {
    if (isContentSetAndUnavailable) {
      if (wasRedirectedFromPreferences) {
        deletePreferenceAndRedirectToDefault()
      } else {
        notifications.add("info", "Unavailable", "The data you were trying to access is unavailable. You've been redirected to the default view.")
        navigate("../", { replace: true })
      }
    }
  }, [isContentSetAndUnavailable, notifications, wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault, navigate])

  useEffect(() => {
    // Asset view display types and Analyze.Trends categories don't always match up because Asset view's dropdown has no filtering logic applied to it
    // Therefore, if the displayType is not in the options(list of categories), set it to the first option
    if (passthroughResourcesStatus === OK && !find(options, { id: displayType })) {
      setDisplayType(get(options, "0.id"))
    }

    const firstOrStartDate = firstDate || startDate
    if (selectedDate && (selectedDate < firstOrStartDate)) {
      resetDate()
    }

    if (litmusViewVisible) {
      navigate(`${window.location.pathname}?daily=true`)
    } else {
      navigate(window.location.pathname)
    }
  }, [passthroughResourcesStatus, options, displayType, setDisplayType, firstDate, startDate, selectedDate, resetDate, litmusViewVisible, navigate])

  function onTabChanged(_, id) {
    setDisplayType(id)
  }

  function onToggleOnly(factorId) {
    toggleOnly(factorId, contentSetId)
  }

  function addSelectedView(factor) {
    addSelectedView(factor.id)
    setDisplayType(factor.id)
  }

  function handleRemoveSelectedView(factorId) {
    if (displayType === factorId) {
      const optionIndex = findIndex(options, option => option.id === factorId)
      setDisplayType(get(options, `${optionIndex - 1}.id`))
    }
    removeSelectedView(factorId)
  }

  const useCurrencyExposure = useMemo(() => isSensitivityContentSet || exposureUnit === METRIC_TYPES.CURRENCY_EXPOSURE, [isSensitivityContentSet, exposureUnit])
  const { detailsFormatter, tickFormat, tooltipFormatter } = useMemo(() => getTrendsExposureFormatters(useCurrencyExposure, currency), [useCurrencyExposure, currency])

  let content
  if (!earliestDate || !latestDate) {
    content = <NoData title="Exposures" className="graph" />
  } else {
    content = <CardContent className={classnames("card-content", { aggregation: displayAggregationToggle })}>
      <Paper className="content">
        <div className="graph">
          <GraphTopBar {...{
              openDialog,
              displayAddView,
              addView: addSelectedView,
              factors: availableSelectViewFactors,
              value: get(selectedOption, "id"),
              onTabChanged: onTabChanged,
              tabs: map(options, ({ id, label, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: () => handleRemoveSelectedView(id),
                label,
              })),
              actions: compact([
                !contentSetId && <DefaultMetricUnits key="unit-selector" metric="exposure" unit={exposureUnit} onChange={setExposureUnit} includeUnitSelector />,
                <ExcelDownload key="excel-download" data={computeCSVData(data, items)} fileName={fileName} track={trackCSVDownload} />,
              ]),
            }}
          />
          {displayAggregationToggle && <AggregationDropdown setValue={setAggregationFn} value={aggregation} listSubheader="Choose display type for style factors" label="Display Type" options={aggregationOptions} classes={{ toggle: "aggregation-toggle" }} />}
          <MultiLine
            tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={tooltipFormatter} />}
            {...{
                ...tickFormat,
                startDate,
                date: selectedDate,
                setDate,
                endDate,
                data,
                onlyItem: only,
                items,
                toggle,
                focusChartLine,
                blurChartLine,
                focusedChartLine,
                toggleOnly: onToggleOnly,
              }}
          />

        </div>
        <HistoricalExposureDetails
          {...{
              selectedDate,
              viewType,
              displayType,
              displayTypeName,
              isStyleFactorsAdditive,
              startDate: firstDate,
              endDate,
              drift,
              selectedOption,
              contentSetId,
              metricType: exposureUnit,
              selectedViews,
              formatter: detailsFormatter,
            }}
        />
      </Paper>
    </CardContent>
  }

  return <div className="modules_analyze-historical-exposure">
    {litmusViewVisible
        ? <Litmus noSpinner selectedDate={selectedDate} contentSetId={contentSetId} />
        : <div className="historical-exposure-wrapper">
          <AnalyzeHeader
            selectedId={contentSetId || "exposure"}
            {...{
                adjusted: datesAdjusted,
                account,
                portfolioId,
                base,
                earliestDate,
                latestDate,
                startDate,
                aiSummaryStartDate,
                endDate,
                selectedDate,
                period,
                setPeriod,
                addNotification,
                resetDate,
                firstDate,
                isActive,
                currency,
                changeDisplayPreferenceType: "navigation.analyze.trends",
              }}
          />
          <LoadingWrapper status={[passthroughResourcesStatus]}>{content}</LoadingWrapper>
        </div>}
  </div>
}

AnalyzeHistoricalExposure.propTypes = {
  account: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  base: PropTypes.object,
  earliestDate: DATE,
  latestDate: DATE,
  startDate: DATE,
  aiSummaryStartDate: DATE,
  endDate: DATE,
  selectedDate: DATE,
  period: PERIOD.isRequired,
  data: PropTypes.array.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  displayType: PropTypes.string.isRequired,
  viewType: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  setPeriod: PropTypes.func.isRequired,
  setDate: PropTypes.func.isRequired,
  setDisplayType: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  trackCSVDownload: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  drift: PropTypes.object.isRequired,
  litmusViewVisible: PropTypes.bool.isRequired,
  focusedChartLine: PropTypes.string,
  fileName: PropTypes.string.isRequired,
  firstDate: PropTypes.string.isRequired,
  resetDate: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  selectedOption: PropTypes.object.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  only: PropTypes.string,
  params: PropTypes.object.isRequired,
  displayTypeName: PropTypes.string.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  datesAdjusted: PropTypes.bool.isRequired,
  displayAggregationToggle: PropTypes.bool.isRequired,
  aggregation: AGGREGATION_SHAPE,
  aggregationOptions: PropTypes.arrayOf(OPTION_SHAPE),
  setAggregationFn: PropTypes.func.isRequired,
  selectedViews: PropTypes.object,
  addSelectedView: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  modelId: PropTypes.string.isRequired,
  displayAddView: PropTypes.bool.isRequired,
  availableSelectViewFactors: PropTypes.array.isRequired,
  removeSelectedView: PropTypes.func.isRequired,
  currency: PropTypes.string,
  isStyleFactorsAdditive: PropTypes.bool.isRequired,
  isSensitivityContentSet: PropTypes.bool.isRequired,
  navigate: PropTypes.func.isRequired,
  setExposureUnit: PropTypes.func.isRequired,
  exposureUnit: METRIC_TYPES_SHAPE.isRequired,
  passthroughResourcesStatus: RESOURCE_STATUS.isRequired,
  isContentSetAndUnavailable: PropTypes.bool.isRequired,
}

export default withNavigate(AnalyzeHistoricalExposure)
